// Practice rooms for /home-key — the course's optional gym. Three rooms of
// short cozy "sits": melodic echoes, chord-job reps, and groove timing. No
// XP, no streaks, no hearts; nothing here gates anything. Rooms draw only on
// what the learner has already met, so they grow as the course does.
//
// Pure and unit-tested, like homeKeyTheory: no audio, no React. Every
// generator takes an injectable `rng` (default Math.random) so tests can
// seed the dice.

import {
  PIANO_RANGE,
  degreeOf,
  minorDegreeOf,
  isInScale,
  chordMidis,
  jobOf,
  minorJobOf,
  CHORD_PERSONA,
  MINOR_CHORD_PERSONA,
  MINOR_TRIADS,
  RAISED_V,
} from "./homeKeyTheory";

/* ============================================================
   The rooms — each unlocks with course progress. Before opening,
   a room shows dimmed with its teaser, tile-map style; after, the
   blurb says what happens inside.
   ============================================================ */

export const ROOMS = [
  {
    id: "echo",
    title: "The Echo Room",
    blurb: "Short calls bounce off these walls. Catch each one by ear and toss it back.",
    opensAfter: "leaving-returning",
    teaser: "A room that answers back. Learn to leave and return, and the door opens.",
  },
  {
    id: "jobs",
    title: "The Jobs Room",
    blurb: "A chord plays; you say what it's doing — home, away, or tension. Reps for your harmonic ear.",
    opensAfter: "seven-chords",
    teaser: "Seven chords are waiting to tell you what they do. Meet them first.",
  },
  {
    id: "pocket",
    title: "The Pocket",
    blurb: "Tap along with the groove and find the warm spot right on the beat.",
    opensAfter: "drums",
    teaser: "There's a groove in here somewhere. Learn kick, snare, hat — then come find it.",
  },
];

// Is this room open yet? Open once its opensAfter lesson is complete.
export const roomOpen = (roomId, completed = {}) => {
  const room = ROOMS.find((r) => r.id === roomId);
  return !!(room && completed[room.opensAfter]);
};

/* ============================================================
   The Echo Room — call generation. Calls live in the established
   phrase model ({ notes: [{ midi, at, dur }] }) so compareEcho and
   playPhrase take them as-is.
   ============================================================ */

// Difficulty tiers. `notes` is the [min, max] call length; the minor tier
// starts on E and is only offered once the learner has met the minor side
// (`requires`). Tiers with no `requires` are always offered.
export const ECHO_TIERS = [
  { id: "t1", label: "two notes", notes: [2, 2] },
  { id: "t2", label: "three notes", notes: [3, 3] },
  { id: "t3", label: "little phrases", notes: [4, 6] },
  { id: "t4", label: "the minor side", notes: [3, 5], minor: true, requires: "new-home" },
];

// Random int in [lo, hi] inclusive.
const randInt = (rng, lo, hi) => lo + Math.floor(rng() * (hi - lo + 1));

// Every scale tone on the on-screen piano, low to high — the ladder the
// calls walk. (The minor side climbs the very same ladder: E natural minor
// is the same seven pitch classes as G major, just re-numbered.)
const SCALE_LADDER = (() => {
  const tones = [];
  for (let m = PIANO_RANGE.low; m <= PIANO_RANGE.high; m += 1) {
    if (isInScale(m)) tones.push(m);
  }
  return tones;
})();

// Where a call may begin: on a home tone. Major calls start on a G (not the
// very top one — leave the phrase room to move); minor calls start on an E.
const HOME_STARTS = SCALE_LADDER.filter(
  (m) => degreeOf(m) === 1 && m < PIANO_RANGE.high
);
const MINOR_STARTS = SCALE_LADDER.filter((m) => minorDegreeOf(m) === 1);

// Call timing, matching the lessons' hand-authored calls: notes ~450ms
// apart, each ringing 350–550ms.
const CALL_SPACING_MS = 450;
const CALL_DUR_MIN_MS = 350;
const CALL_DUR_MAX_MS = 550;

// Accept a tier object or its id; unknown ids land on the first tier.
const resolveTier = (tierOrId) =>
  typeof tierOrId === "string"
    ? ECHO_TIERS.find((t) => t.id === tierOrId) || ECHO_TIERS[0]
    : tierOrId || ECHO_TIERS[0];

// Make one echo call for a tier: starts on a home tone (G, or E on the
// minor side), walks the scale ladder mostly by step with at most one leap
// (a skip of one or two ladder rungs — a third or a fourth), stays diatonic,
// and keeps every note inside PIANO_RANGE.
export const makeEchoCall = (tierOrId, opts = {}) => {
  const rng = opts.rng || Math.random;
  const tier = resolveTier(tierOrId);
  const [minNotes, maxNotes] = tier.notes;
  const count = randInt(rng, minNotes, maxNotes);

  // Longer calls sometimes spend their one leap; two-note calls never do.
  const leapAt =
    count >= 3 && rng() < 0.4 ? randInt(rng, 1, count - 1) : -1;

  const starts = tier.minor ? MINOR_STARTS : HOME_STARTS;
  let idx = SCALE_LADDER.indexOf(starts[randInt(rng, 0, starts.length - 1)]);

  const notes = [];
  for (let i = 0; i < count; i += 1) {
    notes.push({
      midi: SCALE_LADDER[idx],
      at: i * CALL_SPACING_MS,
      dur: randInt(rng, CALL_DUR_MIN_MS, CALL_DUR_MAX_MS),
    });
    if (i + 1 < count) {
      const size = i + 1 === leapAt ? randInt(rng, 2, 3) : 1;
      let dir = rng() < 0.5 ? -1 : 1;
      // If that direction runs off the ladder, go the other way instead —
      // the ladder is far taller than any one move, so one flip always fits.
      if (idx + dir * size < 0 || idx + dir * size >= SCALE_LADDER.length) {
        dir = -dir;
      }
      idx += dir * size;
    }
  }
  return { notes };
};

// A sit's worth of calls for a tier. If the asked-for tier isn't offered
// yet (t4 before the minor side), quietly hand back the highest tier that
// is — the room never scolds.
export const echoSitPlan = (tierId, opts = {}) => {
  const { completed = {}, rng = Math.random, length = 5 } = opts;
  let tier = resolveTier(tierId);
  if (tier.requires && !completed[tier.requires]) {
    const offered = ECHO_TIERS.filter(
      (t) => !t.requires || completed[t.requires]
    );
    tier = offered[offered.length - 1];
  }
  return Array.from({ length }, () => makeEchoCall(tier, { rng }));
};

/* ============================================================
   The Jobs Room — chord-hearing reps. Each entry is ready to play
   (its `midis` go straight to chordPhrase / the audio engine) and
   ready to reveal (`job`, `side`, and the persona word as `label`).
   ============================================================ */

const DEGREES = [1, 2, 3, 4, 5, 6, 7];

// The pool of chords the learner has met so far. Major entries are always
// in (the room itself opens after seven-chords); the minor side joins after
// new-home, and the borrowed doorbell '5+' rings only after minor-moves.
export const jobsPool = (completed = {}) => {
  const pool = DEGREES.map((degree) => ({
    kind: "major",
    degree,
    midis: chordMidis(degree),
    job: jobOf(degree),
    side: "major",
    label: CHORD_PERSONA[degree],
  }));
  if (completed["new-home"]) {
    DEGREES.forEach((degree) => {
      pool.push({
        kind: "minor",
        degree,
        midis: MINOR_TRIADS[degree].slice(),
        job: minorJobOf(degree),
        side: "minor",
        label: MINOR_CHORD_PERSONA[degree],
      });
    });
  }
  if (completed["minor-moves"]) {
    pool.push({
      kind: "raised",
      token: "5+",
      midis: RAISED_V.slice(),
      job: minorJobOf("5+"),
      side: "minor",
      label: MINOR_CHORD_PERSONA["5+"],
    });
  }
  return pool;
};

// Fisher–Yates on a copy, dice supplied by the caller.
const shuffle = (arr, rng) => {
  const out = arr.slice();
  for (let i = out.length - 1; i > 0; i -= 1) {
    const j = Math.floor(rng() * (i + 1));
    [out[i], out[j]] = [out[j], out[i]];
  }
  return out;
};

// A round of reps: shuffled decks of the pool, dealt until the round is
// long enough, with no chord ever repeated back-to-back (even across the
// deck boundary). A round may be longer than the pool — chords come around
// again, just never twice in a row.
export const makeJobsRound = (pool, opts = {}) => {
  const { rng = Math.random, length = 8 } = opts;
  if (!pool || !pool.length) return [];
  const round = [];
  while (round.length < length) {
    const deck = shuffle(pool, rng);
    if (round.length && deck.length > 1 && deck[0] === round[round.length - 1]) {
      const j = randInt(rng, 1, deck.length - 1);
      [deck[0], deck[j]] = [deck[j], deck[0]];
    }
    round.push(...deck);
  }
  return round.slice(0, length);
};

/* ============================================================
   The Pocket — timing analysis. Taps are ms offsets from transport
   start; each snaps to its nearest beat, and the offsets say how the
   sit felt: in the pocket, rushing, dragging, or loose.
   ============================================================ */

// One warm line per feel — never a score, always a hand on the shoulder.
export const POCKET_FEEL_COPY = {
  "in the pocket":
    "Right in the pocket — you and the beat, breathing together.",
  rushing:
    "A little eager — you keep arriving before the beat does. Let it come to you.",
  dragging:
    "A touch behind the beat. Leaning back is a vibe — but try meeting it at the door.",
  loose:
    "Some early, some late — the taps are wandering. Lock onto the hat and let it carry you.",
};

// Analyze a sit's taps against a click grid. `tapsMs` are ms from transport
// start; the grid is `beats` beats at `bpm`. Each tap snaps to its nearest
// beat (when two taps claim one beat, the closer tap wins; beats with no
// tap are simply missed — no penalty, no scolding). Offsets are ms from the
// beat: negative = early, positive = late.
// Returns { perBeat: [{ beat, offsetMs }], avgOffsetMs, spreadMs, hits, feel }
// where spreadMs is the offsets' standard deviation and feel is one of the
// POCKET_FEEL_COPY keys.
export const analyzeTiming = (tapsMs = [], opts = {}) => {
  const { bpm = 92, beats = 16 } = opts;
  const beatMs = 60000 / bpm;

  const byBeat = new Map();
  tapsMs.forEach((tap) => {
    if (!Number.isFinite(tap)) return;
    let beat = Math.round(tap / beatMs);
    if (beat < 0) beat = 0;
    if (beat > beats - 1) beat = beats - 1;
    const offsetMs = tap - beat * beatMs;
    const held = byBeat.get(beat);
    if (held === undefined || Math.abs(offsetMs) < Math.abs(held)) {
      byBeat.set(beat, offsetMs);
    }
  });

  const perBeat = [...byBeat.entries()]
    .sort((a, b) => a[0] - b[0])
    .map(([beat, offsetMs]) => ({ beat, offsetMs }));
  const hits = perBeat.length;
  const avgOffsetMs = hits
    ? perBeat.reduce((sum, p) => sum + p.offsetMs, 0) / hits
    : 0;
  const spreadMs = hits
    ? Math.sqrt(
        perBeat.reduce((sum, p) => sum + (p.offsetMs - avgOffsetMs) ** 2, 0) /
          hits
      )
    : 0;

  let feel;
  if (!hits) feel = "loose"; // nothing landed — nothing to hold onto yet
  else if (avgOffsetMs < -35) feel = "rushing";
  else if (avgOffsetMs > 35) feel = "dragging";
  else if (spreadMs > 45) feel = "loose";
  else feel = "in the pocket";

  return { perBeat, avgOffsetMs, spreadMs, hits, feel };
};

/* ============================================================
   Visits — the only thing practice ever records. No scores persist;
   just that you dropped by, when, and which tier you last chose.
   ============================================================ */

// Next progress state after a room visit — pure: builds a fresh object and
// never touches the one passed in. `tier` is optional; omitting it keeps
// the room's last-chosen tier.
export const practiceVisit = (progress, roomId, tier) => {
  const prev = (progress && progress.practice) || {};
  const room = prev[roomId] || { visits: 0, lastAt: 0, tier: null };
  return {
    ...progress,
    practice: {
      ...prev,
      [roomId]: {
        visits: (room.visits || 0) + 1,
        lastAt: Date.now(),
        tier: tier === undefined ? (room.tier == null ? null : room.tier) : tier,
      },
    },
  };
};
